/**
 * How large the product draws itself — the three steps, their names, and the rule that picks one:
 * the person's own choice on this device, else the company's (`Display.TextScale`), else
 * Comfortable. Moved from the website's `shell/textScale.ts` on 2026-10-14, when the phone's
 * Appearance screen became its second reader (`docs/Mobile_Redesign_Plan.md`, step 11).
 *
 * <p>
 * <strong>The website and the phone apply it differently and agree on the proportion.</strong> The
 * website writes a table of tokens (antd's sizes, the template's custom properties); the phone
 * multiplies every font by {@link textScaleFactor}, which is the website's body size at that step
 * over its body size at Comfortable — 13, 15 and 17 pixels — so Large is the same step larger on
 * both.
 * </p>
 */

export type TextScale = 'compact' | 'comfortable' | 'large';

export const defaultTextScale: TextScale = 'comfortable';

export const textScaleOptions: readonly { value: TextScale; label: string; hint: string }[] = [
  { value: 'compact', label: 'Compact', hint: 'Most rows on screen' },
  { value: 'comfortable', label: 'Comfortable', hint: 'The default' },
  { value: 'large', label: 'Large', hint: 'Easiest to read and to tap' },
];

export function isTextScale(value: string | null | undefined): value is TextScale {
  return value === 'compact' || value === 'comfortable' || value === 'large';
}

/** The person's choice, else the company's, else the default — an unknown stored value is ignored. */
export function resolveTextScale(
  person: string | null | undefined,
  company: string | null | undefined,
): TextScale {
  return isTextScale(person) ? person : isTextScale(company) ? company : defaultTextScale;
}

/** The website's body size at each step, in pixels — the proportion the phone scales by. */
const bodySize: Record<TextScale, number> = { compact: 13, comfortable: 15, large: 17 };

/** How much larger than Comfortable a step draws: 13/15, 1, 17/15. */
export function textScaleFactor(scale: TextScale): number {
  return bodySize[scale] / bodySize.comfortable;
}
